import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { injectIntl } from 'react-intl';
import Image from 'app/utils/image';
import TrapFocus from 'app/utils/accessibility/trapFocus';
import { isEscPressed, isLeftArrowPressed, isRightArrowPressed } from 'app/utils/accessibility/accessibilityUtils';
import MediaItem, { getStaticMediaSrc } from './mediaItem';
import { fullSizeTemplate } from './imageZoomer';

const thumbnailSizes = { s: '96px', l: '96px' };
const fullSizeSizes = { s: '90vw', l: '55vw' };

const isTransformableImage = item => item.type === 'img' && item.format !== 'GIF';

function MediaZoomModal({ items, startIndex, onClose, intl }) {
  const [activeIndex, setActiveIndex] = useState(startIndex);
  const backdropRef = useRef(null);

  const showPrevious = useCallback(
    () => setActiveIndex(index => (index + items.length - 1) % items.length),
    [items.length]
  );
  const showNext = useCallback(() => setActiveIndex(index => (index + 1) % items.length), [items.length]);

  useEffect(() => {
    const htmlElement = document.querySelector('html');
    const backdrop = backdropRef.current;

    const handleKeyDown = event => {
      if (isEscPressed(event)) {
        onClose();
      } else if (isLeftArrowPressed(event)) {
        showPrevious();
      } else if (isRightArrowPressed(event)) {
        showNext();
      }
    };
    const handleBackdropClick = event => {
      if (event.target === backdrop) {
        onClose();
      }
    };

    htmlElement.classList.add('modal-is-opened');
    document.addEventListener('keydown', handleKeyDown);
    backdrop.addEventListener('click', handleBackdropClick);

    return () => {
      htmlElement.classList.remove('modal-is-opened');
      document.removeEventListener('keydown', handleKeyDown);
      backdrop.removeEventListener('click', handleBackdropClick);
    };
  }, [onClose, showPrevious, showNext]);

  return createPortal(
    <TrapFocus enabled>
      <div className="media-zoom" ref={backdropRef}>
        <div className="media-zoom__window" role="dialog" aria-modal="true">
          <button
            type="button"
            className="media-zoom__close icon-close"
            onClick={onClose}
            aria-label={intl.formatMessage({ id: 'header.modal.close' })}
          />
          <div className="media-zoom__main">
            <button
              type="button"
              className="media-zoom__arrow media-zoom__arrow--prev"
              onClick={showPrevious}
              aria-label="previous"
            >
              <i className="fa-light fa-chevron-left" />
            </button>
            <div className="media-zoom__stage">
              <MediaItem key={activeIndex} item={items[activeIndex]} isFullSize sizes={fullSizeSizes} />
              {/* Keeps navigation instant - the browser has the other full size images before they are shown. */}
              <div className="media-zoom__preload" aria-hidden="true">
                {items.map((item, index) =>
                  index !== activeIndex && isTransformableImage(item) ? (
                    <Image
                      key={item.src}
                      media={{ url: item.src, transformationTemplate: fullSizeTemplate }}
                      sizes={fullSizeSizes}
                      alt=""
                    />
                  ) : null
                )}
              </div>
            </div>
            <button
              type="button"
              className="media-zoom__arrow media-zoom__arrow--next"
              onClick={showNext}
              aria-label="next"
            >
              <i className="fa-light fa-chevron-right" />
            </button>
          </div>
          <ul className="media-zoom__thumbs">
            {items.map((item, index) => (
              <li className="media-zoom__thumbs-item" key={`${item.src}_${index + 1}`}>
                <button
                  type="button"
                  className={`media-zoom__thumb${index === activeIndex ? ' media-zoom__thumb--active' : ''}`}
                  onClick={() => setActiveIndex(index)}
                  aria-current={index === activeIndex}
                >
                  {isTransformableImage(item) ? (
                    <Image media={{ url: item.src }} sizes={thumbnailSizes} alt={item?.altText || ''} />
                  ) : (
                    <img src={getStaticMediaSrc(item.src)} alt={item?.altText || ''} />
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </TrapFocus>,
    document.body
  );
}

MediaZoomModal.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  startIndex: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
  intl: PropTypes.object.isRequired,
};

export default injectIntl(MediaZoomModal);
